<script setup>
import { pageTransition } from '~/helpers/animations/animations';
definePageMeta({
  pageTransition: pageTransition,
});

const { t } = useI18n();
const route = useRoute();
const setI18nParams = useSetI18nParams();

const filterSearchTerm = ref(null);
const showPerPage = ref(6);
const shownItems = ref(6);

const loadMore = (numberOfVideos) => {
  if (shownItems.value + showPerPage.value > numberOfVideos) {
    shownItems.value = numberOfVideos;
  } else {
    shownItems.value = shownItems.value + showPerPage.value;
  }
};

const getVideoCode = (url) => {
  const parts = url.split('v=');
  return parts[1];
};

const filterVideos = (videos) => {
  const filteredVideos = [];

  videos.forEach((video) => {
    const regexTitle = new RegExp(filterSearchTerm.value, 'i');

    if (video.title.match(regexTitle)) {
      filteredVideos.push(video);
    }
  });

  return filterSearchTerm.value?.length ? filteredVideos : videos;
};

const videosQuery = gql`
  query videos {
    youtubeCategory {
      id
      title
      description
      videos {
        id
        title
        description
        url
      }
    }
  }
`;

const { data } = await useAsyncQuery(videosQuery);
const youtubeCategory = computed(() => data.value?.youtubeCategory);

const category = computed(() => youtubeCategory.value.find((cat) => useSlugify(cat.title) === route.params.category));

const breadcrumbs = [
  {
    title: t('pages.videogallery.title'),
    route: { name: 'videos' },
  },
  {
    title: category.value.title,
  },
];
</script>

<template>
  <main class="main" v-if="category">
    <Breadcrumbs :items="breadcrumbs" />

    <div class="category__head">
      <h1 class="heading">
        {{ category.title }}
      </h1>
      <a href="https://www.youtube.com/channel/UCeYULpYNcpUJxSeK2FpDpsg" target="_blank" class="button button--tumbleweed">{{ t('global.watch-on-youtube') }}</a>
    </div>

    <div v-if="category" class="mt-5">
      <input id="videosSearch" v-model="filterSearchTerm" type="text" class="input input--search" :placeholder="t('pages.videos.search')" autocomplete="off" />
    </div>

    <div>
      <div v-if="category">
        <div class="category">
          <span class="category-description"> {{ category.description }} </span>
          <div class="videos videos--list">
            <figure
              v-for="(item, index) in filterVideos(category.videos)"
              :key="item.id"
              class="videos__item"
              :class="{
                'videos__item--hidden': index + 1 > shownItems && !filterSearchTerm,
              }"
              :src="item.src || item.url || item.source"
              itemprop="associatedMedia"
              itemscope
              itemtype="http://schema.org/ImageObject"
            >
              <nuxt-link
                itemprop="contentUrl"
                :to="
                  localePath({
                    name: 'videos-category-id',
                    params: { id: item.id, category: useSlugify(category.title) },
                  })
                "
                class="video-strip__item"
              >
                <img :id="'video_' + getVideoCode(item.url)" :src="'https://img.youtube.com/vi/' + getVideoCode(item.url) + '/0.jpg'" alt="Youtube thumb" />
                <span>{{ item.title }}</span>
              </nuxt-link>
            </figure>
          </div>
        </div>

        <div v-if="!filterSearchTerm" class="mt-4">
          <div class="category__actions">
            <button class="button button--small" :disabled="shownItems >= filterVideos(category.videos).length" @click="loadMore(filterVideos(category.videos).length)">
              {{ t('global.load-more') }}
            </button>
            <div>
              {{ t('global.showing') }}
              {{ filterVideos(category.videos).length < shownItems ? filterVideos(category.videos).length : shownItems }}
              / {{ filterVideos(category.videos).length || 0 }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.video-strip__item {
  width: 280px;
  display: flex;
  flex-direction: column;
  font-weight: bold;
  font-size: 15px;
  line-height: 17px;

  img {
    margin-bottom: 11px;
  }
}
.category {
  border-top: 1px solid #000;
  padding-top: 10px;
  margin-top: 40px;

  h4 {
    font-weight: bold;
    margin-bottom: 16px;
  }
}

.category-description {
  display: block;
  margin-bottom: 16px;
}

.category__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.gallery .swiper-slide {
  height: auto;
}

.videos {
  &--list {
    margin: 0 -14px;
    display: flex;
    flex-wrap: wrap;
  }
}

.videos__item {
  margin: 14px;

  &--hidden {
    display: none;
  }
}

.category__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
